import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input } from 'antd';
import type { CheckboxChangeEvent, GetProp } from 'antd';
import './CheckboxFilterDropdown.scss';

export type CheckboxFilterOption = {
  label: string;
  value: string;
};

interface CheckboxFilterDropdownProps {
  options: CheckboxFilterOption[];
  selectedKeys: string[];
  onApply: (keys: string[]) => void;
  onReset: () => void;
  onTempKeysChange?: (keys: string[]) => void;
}

const CheckboxFilterDropdown = ({ options, selectedKeys, onApply, onReset, onTempKeysChange }: CheckboxFilterDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingKeys, setPendingKeys] = useState<string[]>(selectedKeys);

  useEffect(() => {
    setPendingKeys(selectedKeys);
  }, [selectedKeys]);

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, searchTerm]);

  const allValues = useMemo(() => options.map((option) => option.value), [options]);
  const allChecked = options.length > 0 && allValues.every((value) => pendingKeys.includes(value));
  const indeterminate = pendingKeys.length > 0 && !allChecked;

  const updatePendingKeys = (keys: string[]) => {
    setPendingKeys(keys);
    onTempKeysChange?.(keys);
  };

  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      updatePendingKeys(allValues);
    } else {
      updatePendingKeys([]);
    }
  };

  const handleGroupChange = (values: GetProp<typeof Checkbox.Group, 'value'>) => {
    const normalized = values.map((value) => String(value));
    updatePendingKeys(normalized);
  };

  const handleReset = () => {
    updatePendingKeys([]);
    onReset();
  };

  return (
    <div className="checkbox-filter-dropdown">
      <Checkbox checked={allChecked} indeterminate={indeterminate} onChange={handleSelectAllChange}>
        Select All
      </Checkbox>
      <Input
        size="small"
        allowClear
        placeholder="Search options"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        className="checkbox-filter-search"
      />
      <div className="checkbox-filter-list">
        <Checkbox.Group value={pendingKeys} onChange={handleGroupChange}>
          {filteredOptions.map((option) => (
            <Checkbox key={option.value} value={option.value} className="checkbox-filter-item">
              {option.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>
      <div className="checkbox-filter-actions">
        <Button size="small" onClick={handleReset}>
          Reset
        </Button>
        <Button size="small" type="primary" onClick={() => onApply(pendingKeys)}>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default CheckboxFilterDropdown;
